import { createContext, useCallback, useContext, useEffect, useMemo, useRef } from "react";
import { useOL } from "rlayers";

const MapHandlersContext = createContext({ register: () => () => {} });

export const MapHandlersProvider = ({ children }) => {
    const { map } = useOL();
    const handlersRef = useRef([]);

    const unregister = useCallback((handler) => {
        if (!handler) {
            return;
        }
        handlersRef.current = handlersRef.current.filter((entry) => entry.callback !== handler);
    }, []);

    const register = useCallback((handler, priority = 0) => {
        if (!handler) {
            return () => {};
        }
        const handlers = handlersRef.current;
        const existing = handlers.find((entry) => entry.callback === handler);
        if (existing) {
            existing.priority = priority;
        } else {
            handlers.push({ callback: handler, priority });
        }
        handlers.sort((a, b) => b.priority - a.priority);
        return () => unregister(handler);
    }, [unregister]);

    const dispatch = useCallback(async (event) => {
        const handlers = handlersRef.current;
        for (const entry of handlers) {
            const handled = await entry.callback(event);
            if (handled) {
                return true;
            }
        }
        return false;
    }, []);

    useEffect(() => {
        if (!map) {
            return undefined;
        }
        const clickListener = (event) => {
            void dispatch(event);
        };
        map.on('singleclick', clickListener);
        return () => {
            map.un('singleclick', clickListener);
        };
    }, [map, dispatch]);

    const value = useMemo(() => ({ register }), [register]);

    return <MapHandlersContext.Provider value={value}>{children}</MapHandlersContext.Provider>;
};

export const useMapHandlers = () => {
    return useContext(MapHandlersContext);
};